import { keyframes } from '@emotion/react';
import { useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useLayoutNav } from 'src/hooks/use-layout-nav';

import { roles, hasPermissions } from 'src/utils/roles';

import { allLangs } from 'src/locales';
import { CONFIG } from 'src/config-global';
import { varAlpha, stylesMode } from 'src/theme/styles';
import { useChatWidgetStore } from 'src/stores/chat-widget';

import { bulletColor } from 'src/components/nav-section';
import { useSettingsContext } from 'src/components/settings';

import { ChatWidget } from 'src/sections/chat/components/chat-widget';

import { useAuthContext } from 'src/auth/hooks';

import { Main } from './main';
import { NavMobile } from './nav-mobile';
import { layoutClasses } from '../classes';
import { NavVertical } from './nav-vertical';
import { NavHorizontal } from './nav-horizontal';
import { _account } from '../config-nav-account';
import { HeaderBase } from '../core/header-base';
import { LayoutSection } from '../core/layout-section';
import { navData as dashboardNavData } from '../config-nav-dashboard';

// ----------------------------------------------------------------------

// Sidebar and header slide in once per app load. Each route section mounts its own
// DashboardLayout, so a module-level flag keeps them still when moving between sections.
let hasPlayedIntro = false;

const navEnter = keyframes`
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: none; }
`;

const headerEnter = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: none; }
`;

export function DashboardLayout({ sx, children, data }) {
  const theme = useTheme();

  const [playIntro] = useState(() => !hasPlayedIntro);

  useEffect(() => {
    hasPlayedIntro = true;
  }, []);

  const introAnimation = (name, delay = 0) =>
    playIntro && {
      animation: `${name} 480ms ${theme.transitions.easing.easeOut} ${delay}ms both`,
    };
  const location = useLocation();
  const router = useRouter();
  const isChatRoute = location.pathname === paths.chat;

  const { user } = useAuthContext();
  const canUseGlobalInsights = hasPermissions(user, [roles.AI_GLOBAL]);

  const chatOpen = useChatWidgetStore((state) => state.open);
  const chatWidth = useChatWidgetStore((state) => state.width);
  const chatIsResizing = useChatWidgetStore((state) => state.isResizing);
  const reportViewActive = useChatWidgetStore((state) => state.reportViewActive);
  const toggleChatWidget = useChatWidgetStore((state) => state.toggle);
  const openGlobalChat = useChatWidgetStore((state) => state.openGlobal);
  const closeChatWidget = useChatWidgetStore((state) => state.close);
  const chatToggle = useCallback(() => {
    if (isChatRoute) {
      // Already on the full chat page — the icon can't toggle a widget that isn't shown
      // there, so make it start a new chat instead of being a no-op.
      router.push(`${paths.chat}?compose=${Date.now()}`);
      return;
    }
    if (reportViewActive) {
      openGlobalChat();
      return;
    }
    toggleChatWidget();
  }, [isChatRoute, reportViewActive, openGlobalChat, toggleChatWidget, router]);
  const chatClose = useCallback(() => closeChatWidget(), [closeChatWidget]);
  const chatWidgetVisible = chatOpen && !isChatRoute;

  useEffect(() => {
    if (isChatRoute) {
      chatClose();
    }
  }, [isChatRoute, chatClose]);

  const mobileNavOpen = useBoolean();

  const settings = useSettingsContext();

  const navColorVars = useNavColorVars(theme, settings);

  const navData = useLayoutNav(dashboardNavData, canUseGlobalInsights);

  const layoutQuery = 'lg';

  const isNavMini = settings.navLayout === 'mini';

  const isNavHorizontal = settings.navLayout === 'horizontal';

  const isNavVertical = isNavMini || settings.navLayout === 'vertical';

  return (
    <>
      <NavMobile
        data={navData}
        open={mobileNavOpen.value}
        onClose={mobileNavOpen.onFalse}
        cssVars={navColorVars.section}
        slotProps={{ currentRoles: user?.roles, currentFeatures: user?.features }}
      />

      <LayoutSection
        /** **************************************
         * Header
         *************************************** */
        headerSection={
          <HeaderBase
            layoutQuery={layoutQuery}
            disableElevation={isNavVertical}
            onOpenNav={mobileNavOpen.onTrue}
            onChatToggle={chatToggle}
            chatOpen={chatOpen}
            data={{
              nav: navData,
              langs: allLangs,
              account: _account,
              contacts: [],
              notifications: [],
            }}
            slotsDisplay={{
              signIn: false,
              purchase: false,
              helpLink: false,
              chat: canUseGlobalInsights,
              environmentIndicator: CONFIG.build.environment !== 'production',
              screenSizeIndicator: CONFIG.build.environment === 'local'
            }}
            slots={{
              topArea: (
                <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                  This is an info Alert.
                </Alert>
              ),
              bottomArea: isNavHorizontal ? (
                <NavHorizontal
                  data={navData}
                  layoutQuery={layoutQuery}
                  cssVars={navColorVars.section}
                />
              ) : null,
            }}
            slotProps={{
              toolbar: {
                sx: {
                  [`& [data-slot="logo"]`]: {
                    display: 'none',
                  },
                  [`& [data-area="right"]`]: {
                    gap: { xs: 0, sm: 0.75 },
                  },
                  ...(isNavHorizontal && {
                    bgcolor: 'var(--layout-nav-bg)',
                    [`& .${iconButtonClasses.root}`]: {
                      color: 'var(--layout-nav-text-secondary-color)',
                    },
                    [theme.breakpoints.up(layoutQuery)]: {
                      height: 'var(--layout-nav-horizontal-height)',
                    },
                    [`& [data-slot="workspaces"]`]: {
                      color: 'var(--layout-nav-text-primary-color)',
                    },
                    [`& [data-slot="logo"]`]: {
                      display: 'none',
                      [theme.breakpoints.up(layoutQuery)]: {
                        display: 'inline-flex',
                      },
                    },
                    [`& [data-slot="divider"]`]: {
                      [theme.breakpoints.up(layoutQuery)]: {
                        display: 'flex',
                      },
                    },
                  }),
                },
              },
              container: {
                maxWidth: false,
                sx: {
                  ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
                },
              },
            }}
          />
        }
        /** **************************************
         * Sidebar
         *************************************** */
        sidebarSection={
          isNavHorizontal ? null : (
            <NavVertical
              data={navData}
              isNavMini={isNavMini}
              layoutQuery={layoutQuery}
              cssVars={navColorVars.section}
              slotProps={{ currentRoles: user?.roles, currentFeatures: user?.features }}
              sx={introAnimation(navEnter)}
              onToggleNav={() =>
                settings.onUpdateField(
                  'navLayout',
                  settings.navLayout === 'vertical' ? 'mini' : 'vertical'
                )
              }
            />
          )
        }
        /** **************************************
         * Footer
         *************************************** */
        footerSection={null}
        /** **************************************
         * Style
         *************************************** */
        cssVars={{
          ...navColorVars.layout,
          '--layout-transition-easing': 'linear',
          '--layout-transition-duration': '120ms',
          '--layout-nav-mini-width': '88px',
          '--layout-nav-vertical-width': '300px',
          '--layout-nav-horizontal-height': '64px',
          '--layout-dashboard-content-pt': theme.spacing(1),
          '--layout-dashboard-content-pb': theme.spacing(8),
          '--layout-dashboard-content-px': theme.spacing(5),
        }}
        sx={{
          [`& .${layoutClasses.header}`]: introAnimation(headerEnter, 80),
          [`& .${layoutClasses.hasSidebar}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
            },
          },
          ...sx,
        }}
      >
        <Main
          isNavHorizontal={isNavHorizontal}
          sx={{
            // Skip the animated transition while the sidebar is being dragged, otherwise the
            // reserved space lags behind the pointer instead of tracking it 1:1.
            transition: chatIsResizing
              ? 'none'
              : theme.transitions.create(['padding-right'], {
                  easing: 'var(--layout-transition-easing)',
                  duration: 'var(--layout-transition-duration)',
                }),
            [theme.breakpoints.up('md')]: {
              pr: chatWidgetVisible ? `${chatWidth}px` : 0,
            },
          }}
        >
          {children}
        </Main>
        {!isChatRoute && <ChatWidget open={chatOpen} onClose={chatClose} />}
      </LayoutSection>
    </>
  );
}

// ----------------------------------------------------------------------

function useNavColorVars(theme, settings) {
  const {
    vars: { palette },
  } = theme;

  return useMemo(() => {
    switch (settings.navColor) {
      case 'integrate':
        return {
          layout: {
            '--layout-nav-bg': palette.background.default,
            '--layout-nav-horizontal-bg': varAlpha(palette.background.defaultChannel, 0.8),
            '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], 0.12),
            '--layout-nav-text-primary-color': palette.text.primary,
            '--layout-nav-text-secondary-color': palette.text.secondary,
            '--layout-nav-text-disabled-color': palette.text.disabled,
            [stylesMode.dark]: {
              '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], 0.08),
              '--layout-nav-horizontal-bg': varAlpha(palette.background.defaultChannel, 0.96),
            },
          },
          section: {},
        };
      case 'apparent':
        return {
          layout: {
            '--layout-nav-bg': palette.grey[900],
            '--layout-nav-horizontal-bg': varAlpha(palette.grey['900Channel'], 0.96),
            '--layout-nav-border-color': 'transparent',
            '--layout-nav-text-primary-color': palette.common.white,
            '--layout-nav-text-secondary-color': palette.grey[500],
            '--layout-nav-text-disabled-color': palette.grey[600],
            [stylesMode.dark]: {
              '--layout-nav-bg': palette.grey[800],
              '--layout-nav-horizontal-bg': varAlpha(palette.grey['800Channel'], 0.8),
            },
          },
          section: {
            // caption
            '--nav-item-caption-color': palette.grey[600],
            // subheader
            '--nav-subheader-color': palette.grey[600],
            '--nav-subheader-hover-color': palette.common.white,
            // item
            '--nav-item-color': palette.grey[500],
            '--nav-item-root-active-color': palette.primary.light,
            '--nav-item-root-open-color': palette.common.white,
            // bullet
            '--nav-bullet-light-color': bulletColor.dark,
            // sub
            ...(settings.navLayout === 'vertical' && {
              '--nav-item-sub-active-color': palette.common.white,
              '--nav-item-sub-open-color': palette.common.white,
            }),
          },
        };
      default:
        throw new Error(`Invalid color: ${settings.navColor}`);
    }
  }, [
    palette.background.default,
    palette.background.defaultChannel,
    palette.common.white,
    palette.grey,
    palette.primary.light,
    palette.text.disabled,
    palette.text.primary,
    palette.text.secondary,
    settings.navColor,
    settings.navLayout,
  ]);
}
